import { useState } from "react";
import { Chip } from "@vb/ui";
import { configForCurrency } from "@vb/rgs-contract";
import { useGame } from "../store.js";
import { AutoplayPanel } from "./AutoplayPanel.js";

interface BetInputProps {
  amount: number;
  currency: string;
  disabled: boolean;
  onChange: (v: number) => void;
  maxBalance: number;
}

/** Render the bet amount at full precision for the currency. Used for the
 *  text input value; the wallet chip uses the compactDecimals variant. */
function formatBet(amount: number, currency: string): string {
  const cfg = configForCurrency(currency);
  return amount.toFixed(cfg.decimals);
}

type Mode = "manual" | "auto";

export function BetInput({ amount, currency, disabled, onChange, maxBalance }: BetInputProps) {
  const [mode, setMode] = useState<Mode>("manual");
  const autoplayActive = useGame((s) => s.autoplay.active);
  // Force auto tab visible while running so the user can see the status / STOP button.
  const effectiveMode: Mode = autoplayActive ? "auto" : mode;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        role="tablist"
        aria-label="Betting mode"
        style={{
          display: "inline-flex",
          gap: 0,
          alignSelf: "flex-start",
          background: "var(--bg-2)",
          border: "1px solid var(--line)",
          borderRadius: 8,
          padding: 2,
        }}
      >
        <ModeTab
          active={effectiveMode === "manual"}
          onClick={() => !autoplayActive && setMode("manual")}
          disabled={autoplayActive}
        >
          Manual
        </ModeTab>
        <ModeTab
          active={effectiveMode === "auto"}
          onClick={() => setMode("auto")}
        >
          Auto
        </ModeTab>
      </div>

      {effectiveMode === "manual" ? (
        <ManualPanel
          amount={amount}
          currency={currency}
          disabled={disabled}
          onChange={onChange}
          maxBalance={maxBalance}
        />
      ) : (
        <AutoplayPanel disabled={disabled} />
      )}
    </div>
  );
}

function ModeTab({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 14px",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        border: "none",
        borderRadius: 6,
        cursor: disabled ? "not-allowed" : "pointer",
        background: active ? "var(--bg-3)" : "transparent",
        color: active ? "var(--fg-1)" : "var(--fg-3)",
      }}
    >
      {children}
    </button>
  );
}

function ManualPanel({
  amount,
  currency,
  disabled,
  onChange,
  maxBalance,
}: BetInputProps) {
  // Currency-aware presets:
  //  - "½" / "2×" scale relative to current bet (works for any currency).
  //  - "Max" honors the per-currency cap from CURRENCY_CONFIG instead of the
  //    legacy 0.05 (BTC-magnitude) ceiling. For USDT that was $0.05, broken.
  //  - Three quick-stake chips inject sensible starting amounts in the
  //    currency's natural magnitude (BTC: 0.0001/0.001/0.01, USDT: 10/50/100).
  const cfg = configForCurrency(currency);
  const presets: Array<[string, () => number]> = [
    [`${cfg.quickStakes[0]}`, () => cfg.quickStakes[0]],
    [`${cfg.quickStakes[1]}`, () => cfg.quickStakes[1]],
    [`${cfg.quickStakes[2]}`, () => cfg.quickStakes[2]],
    ["½", () => amount / 2],
    ["2×", () => amount * 2],
    ["Max", () => Math.min(maxBalance, cfg.maxBet)],
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ fontSize: 10 }}>
        Bet amount
      </span>
      <div
        className="vb-input"
        style={{
          padding: "8px 12px",
          fontSize: 13,
          ...(disabled ? { opacity: 0.5 } : {}),
        }}
      >
        <input
          inputMode="decimal"
          value={formatBet(amount, currency)}
          disabled={disabled}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n) && n >= 0) onChange(n);
          }}
        />
        <span className="unit" style={{ fontSize: 10 }}>
          {currency}
        </span>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {presets.map(([label, fn]) => (
          <Chip
            key={label}
            style={{
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
              padding: "4px 10px",
              fontSize: 12,
            }}
            onClick={() => {
              if (disabled) return;
              // Round to the currency's authoritative decimals (BTC=8, USDT=2)
              // so we never push sub-precision dust through to the server.
              onChange(Number(fn().toFixed(configForCurrency(currency).decimals)));
            }}
          >
            {label}
          </Chip>
        ))}
      </div>
    </div>
  );
}
